import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { axisCount, rigFromModel } from '@/domain/rig'
import { Alert, Card, Spinner } from '@/components/ui'
import { PageHead } from '@/sim/parts'
import { useSimulation } from '@/sim/SimulationProvider'
import { fillReference, referenceValues } from '@/sim/referenceValues'
import '@/sim/reference.css'

/**
 * Die Fachkapitel aus der Vorlage.
 *
 * WARUM DER TEXT NICHT IN JSX STEHT.
 *
 * Es sind rund 1,6 MB: 283 Tabellen, 23 Zeichnungen, 20 Bilder, dazu die
 * Formeln. Ihn nach JSX zu übersetzen wäre eine mechanische Arbeit mit
 * tausenden Gelegenheiten, eine Zahl zu verlieren — und jede spätere
 * Aktualisierung der Vorlage wäre dieselbe Arbeit noch einmal. Er liegt deshalb
 * als Inhaltsdatei unter `public/reference/` und wird unverändert eingesetzt.
 *
 * DREI FOLGEN, DIE MAN KENNEN MUSS:
 *
 * 1. `dangerouslySetInnerHTML`. Das ist hier richtig und nicht gefährlich: der
 *    Inhalt ist eigenes, ausgeliefertes Material aus dem Repository, keine
 *    Nutzereingabe. Er enthält nachweislich kein `<script>`, keinen
 *    `on…`-Attributhandler und keine externe Ressource — nur Text, Tabellen,
 *    SVG und Bilder als Daten-URI. Eine Prüfung hält das fest.
 *
 * 2. Der Text kommt NICHT ins Bündel. Er wird beim Öffnen der Seite geladen und
 *    danach vom Browser zwischengespeichert. Eine Tabellenansicht zahlt nichts
 *    dafür, dass es das Marktdossier gibt.
 *
 * 3. Die Klassennamen sind die der Vorlage. `reference.css` belegt dafür deren
 *    Variablen mit unseren Tokens — der Text folgt damit der Marke und beiden
 *    Registern, ohne dass eine Zeile Inhalt angefasst wurde.
 */

export interface ReferencePageProps {
  /** Dateiname unter `public/reference/`, ohne Endung. */
  slug: string
  title: string
  /**
   * Die Seite bettet die drei parametrischen Auslegungsmodelle ein.
   *
   * Nur `konzept` tut das. Die Modelle sind eigenständige HTML-Dokumente und
   * laufen in einem Rahmen — abgeschottet, ohne Netz, ohne Zugriff auf die
   * Simulation.
   */
  tools?: boolean
}

/** Die drei Auslegungsmodelle, in der Reihenfolge der Umschaltleiste. */
const TOOLS: Record<string, { file: string; note: string }> = {
  waist: {
    file: '/tools/rumpfgelenk.html',
    note: 'Kurbeln, Schubstangen, Kreuzgelenk — Kapitel 21.1',
  },
  hip: { file: '/tools/huefte.html', note: 'Gieren, Rollen, Nicken in Reihe — Kapitel 21.2' },
  shank: {
    file: '/tools/unterschenkel.html',
    note: 'Kurbelschwinge und Parallelkinematik — Kapitel 20',
  },
}

/**
 * Verteilt die Beschriftungen der Aufbauzeichnung.
 *
 * DAS EINZIGE STÜCK VERHALTEN, DAS NICHT IM INHALT STECKT. Die Zeichnung hat
 * rund vierzig Ankerpunkte am Körper; die Beschriftungen dazu liegen links und
 * rechts daneben. Wo genau, kann die Zeichnung nicht wissen — das hängt davon
 * ab, WELCHE Ebenen gerade sichtbar sind. Ohne diese Verteilung liegen alle
 * Beschriftungen einer Seite übereinander und die Zeichnung ist unlesbar.
 *
 * Das Verfahren: sichtbare Beschriftungen je Seite nach ihrer Ankerhöhe
 * sortieren, gleichmässig über die Zeichnung verteilen, dann die Führungslinie
 * vom Anker über einen Knick zur Beschriftung neu ziehen. Wird der Abstand zu
 * klein, bekommt die Zeichnung `dense` und schaltet auf kleinere Schrift.
 *
 * Aus der Vorlage übernommen; die Zahlen sind ihre.
 */
function layoutAnatomy(host: HTMLElement | null): void {
  const svg = host?.querySelector('.anatsvg')
  if (!svg) return

  const TOP = 96
  const BOTTOM = 940
  const MIN_GAP = 30
  let dense = false

  for (const side of ['l', 'r']) {
    const labels = Array.from(
      svg.querySelectorAll<SVGGElement>(`.lyr:not(.off) .lab[data-s="${side}"]`),
    ).sort((a, b) => Number(a.dataset.ay) - Number(b.dataset.ay))

    if (labels.length === 0) continue

    const span = BOTTOM - TOP
    const gap =
      labels.length > 1 ? Math.min(56, Math.max(MIN_GAP * 0.72, span / (labels.length - 1))) : 0
    if (gap < MIN_GAP) dense = true

    const start = TOP + Math.max(0, (span - gap * (labels.length - 1)) / 2)

    labels.forEach((label, index) => {
      const y = start + index * gap
      const { ax, ay, kx, tx } = label.dataset
      label.querySelector('.lead')?.setAttribute('points', `${ax},${ay} ${kx},${y} ${tx},${y}`)
      const texts = label.querySelectorAll('text')
      texts[0]?.setAttribute('y', String(y - 4))
      texts[1]?.setAttribute('y', String(y + 13))
    })
  }

  svg.classList.toggle('dense', dense)
}

export function ReferencePage({ slug, title, tools }: ReferencePageProps) {
  return (
    <div className="stack">
      <PageHead title={title} />
      <ReferenceBody slug={slug} tools={tools} />
    </div>
  )
}

/**
 * Nur der Fachtext, ohne Seitenkopf.
 *
 * WOZU DIE TRENNUNG. Die Kachel Umsetzung ist keine Fachtextseite: sie rechnet
 * oben und zeigt darunter die vier Kapitel, die aus dem Konzept hierher gezogen
 * sind. Sie braucht den Textteil ohne dessen Überschrift, weil sie eine eigene
 * hat — zwei Seitenköpfe übereinander wären zwei Antworten auf die Frage, wo
 * man ist.
 */
export function ReferenceBody({ slug, tools }: { slug: string; tools?: boolean }) {
  const host = useRef<HTMLDivElement>(null)
  const [raw, setRaw] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  /*
    DER FACHTEXT HOLT SEINE ZAHLEN AUS DEM MODELL.

    In den Kapiteln standen Eckdaten im Fliesstext — Bauhöhe, Masse, Anzahl der
    Freiheitsgrade. Sie waren richtig, als sie geschrieben wurden, und sind es
    danach nie wieder geworden: der Simulator rechnete weiter, der Text nicht.
    Wer eine Masse ändert, sah auf jeder Seite die neue Zahl und im Kapitel
    daneben die alte.

    Jetzt schreibt der Text `{{koerpermasse}}`, und hier wird eingesetzt. Die
    Werteliste steht in `referenceValues.ts`; ein Platzhalter ohne Wert bleibt
    sichtbar stehen, statt still zu verschwinden.
  */
  const { model, state } = useSimulation()
  const werte = useMemo(
    () =>
      referenceValues({
        model,
        state,
        /* Über den Achsbaum gezählt und nicht hier: `axisCount` ist die
           Zählung, die auch der Achsbaum und die Antriebsliste benutzen. Eine
           zweite wäre irgendwann eine andere. */
        activeDofs: axisCount(rigFromModel(model, state.off)),
      }),
    [model, state],
  )

  const html = useMemo(() => (raw === null ? null : fillReference(raw, werte)), [raw, werte])

  useEffect(() => {
    let alive = true
    setRaw(null)
    setFailed(false)

    fetch(`${import.meta.env.BASE_URL}reference/${slug}.html?v=${__BUILD__}`)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status))
        return response.text()
      })
      .then((text) => {
        if (alive) setRaw(text)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })

    return () => {
      alive = false
    }
  }, [slug])

  /**
   * Die Sprungmarken bleiben IM DOKUMENT.
   *
   * Ein `<a href="#k12">` würde sonst die Adresse der Anwendung ändern und den
   * Router auf den Plan rufen. Der Klick wird deshalb abgefangen und das Ziel
   * von Hand angesprungen — mit `scrollIntoView`, damit die Bewegung sichtbar
   * ist und man merkt, dass man gesprungen ist.
   */
  const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement | null
    if (anchor) {
      event.preventDefault()
      const id = anchor.getAttribute('href')!.slice(1)
      host.current?.querySelector(`#${CSS.escape(id)}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      return
    }

    /* Die Ebenenschalter der Aufbauzeichnung: sie blenden eine Ebene der
       Zeichnung UND den zugehörigen Abschnitt darunter aus. Beides gehört
       zusammen — eine ausgeblendete Ebene mit sichtbarem Text wäre eine
       Beschreibung von etwas, das man nicht sieht. */
    const chip = target.closest('.anchip') as HTMLElement | null
    if (chip) {
      const key = chip.dataset.l
      if (!key) return
      chip.classList.toggle('on')
      const on = chip.classList.contains('on')
      host.current?.querySelector(`.lyr-${key}`)?.classList.toggle('off', !on)
      host.current?.querySelector(`.andet[data-l="${key}"]`)?.classList.toggle('off', !on)
      /* Die Beschriftungen verteilen sich neu — eine Ebene weniger heisst mehr
         Platz für die übrigen. */
      layoutAnatomy(host.current)
      return
    }

    /* Die Umschaltleiste der Auslegungsmodelle. */
    const tab = target.closest('#toolPick button') as HTMLElement | null
    if (tab) {
      const key = tab.dataset.t
      const tool = key ? TOOLS[key] : undefined
      if (!tool) return
      const frame = host.current?.querySelector('#toolFrame') as HTMLIFrameElement | null
      const src = `${tool.file}?v=${__BUILD__}`
      if (frame && frame.getAttribute('src') !== src) frame.setAttribute('src', src)
      const note = host.current?.querySelector('#toolNote')
      if (note) note.textContent = tool.note
      host.current
        ?.querySelectorAll('#toolPick button')
        .forEach((button) => button.classList.toggle('on', button === tab))
    }
  }, [])

  /* Die Zeichnung wird verteilt, sobald sie im Baum steht. */
  useEffect(() => {
    if (html) layoutAnatomy(host.current)
  }, [html])

  /*
    WELCHES KAPITEL GERADE OBEN STEHT.

    Die Leiste zeigte bis hierher nur, WAS es gibt. In einem Text mit
    einundzwanzig Kapiteln ist die zweite Frage aber die häufigere: wo bin ich?
    Beobachtet werden die Überschriften; die oberste, die noch im Bild ist,
    markiert ihren Knopf.

    Der Rand oben schneidet die klebende Leiste weg — ohne ihn gilt eine
    Überschrift als sichtbar, während sie längst darunter verschwunden ist.
  */
  useEffect(() => {
    const root = host.current
    if (!html || !root) return
    const heads = [...root.querySelectorAll<HTMLElement>('h3[id]')]
    if (heads.length === 0) return

    const seen = new Set<string>()
    const mark = () => {
      const links = root.querySelectorAll<HTMLAnchorElement>('.toc a')
      /* Das zuletzt begonnene Kapitel gewinnt — nicht das erste sichtbare:
         beim Herunterscrollen steht das neue oben und das alte noch im Bild. */
      let active = ''
      for (const head of heads) if (seen.has(head.id)) active = head.id
      links.forEach((a) => a.classList.toggle('on', a.getAttribute('href') === `#${active}`))
    }

    const watch = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) seen.add(entry.target.id)
          else seen.delete(entry.target.id)
        }
        mark()
      },
      { rootMargin: '-90px 0px -70% 0px' },
    )
    for (const head of heads) watch.observe(head)
    return () => watch.disconnect()
  }, [html])

  /* Das erste Modell wird geladen, sobald der Text steht — die Leiste zeigt
     sonst eine aktive Schaltfläche über einem leeren Rahmen. */
  useEffect(() => {
    if (!tools || !html) return
    const frame = host.current?.querySelector('#toolFrame') as HTMLIFrameElement | null
    if (frame && !frame.getAttribute('src'))
      frame.setAttribute('src', `${TOOLS.waist.file}?v=${__BUILD__}`)
  }, [tools, html])

  return (
    <>
      {failed && (
        <Alert tone="danger" title="Der Text liess sich nicht laden">
          Die Inhaltsdatei <code>reference/{slug}.html</code> war nicht erreichbar. Sie wird mit der
          Anwendung ausgeliefert; fehlt sie, ist das ein Fehler im Auslieferungsstand und keine
          Frage der Verbindung.
        </Alert>
      )}

      {!html && !failed && (
        <Card>
          <div className="ref__loading">
            <Spinner />
            <span>Fachtext wird geladen …</span>
          </div>
        </Card>
      )}

      {html && (
        <Card flush>
          <div
            ref={host}
            className={`doc${slug === 'markt' ? ' mkt' : ''}`}
            onClick={onClick}
            /* Siehe Kopfkommentar: eigenes, geprüftes Material aus dem Repo. */
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Card>
      )}
    </>
  )
}
